import { MutationTree } from 'vuex';
import {
    PrivilegeIdLendingRecordState,
    TextFile,
    PostPrivilegeIdLendingRecords,
    PrivilegeIdLendingRecord,
    PrivilegeIdLendingRecordEdit,
    PostRoutineWork,
} from './types';
import * as types from './mutation-types';
import { ErrorDetail } from '../../types';
import { COMMENT_FAILURE, COMMENT_SUCCESS } from './mutation-types';

// エラーをクリアする
const resetErrors = (state: PrivilegeIdLendingRecordState): void => {
    const csvState = state.csv;
    csvState.error = false;
    csvState.errorTitle = undefined;
    csvState.errorDetails = undefined;

    const showState = state.show;
    showState.error = false;
    showState.errorCode = undefined;
    showState.errorMsg = undefined;

    const videoFileState = state.videoFile;
    videoFileState.error = false;
    videoFileState.errorTitle = undefined;
    videoFileState.errorDetails = undefined;

    const textFileState = state.textFile;
    textFileState.error = false;
    textFileState.errorTitle = undefined;
    textFileState.errorDetails = undefined;

    const recFileState = state.textFile;
    recFileState.error = false;
    recFileState.errorTitle = undefined;
    recFileState.errorDetails = undefined;

    const privilegeIdLendingRecordsState = state.privilegeIdLendingRecords;
    privilegeIdLendingRecordsState.error = false;
    privilegeIdLendingRecordsState.errorTitle = undefined;
    privilegeIdLendingRecordsState.errorDetails = undefined;

    const routineWorkState = state.routineWork;
    routineWorkState.error = false;
    routineWorkState.errorTitle = undefined;
    routineWorkState.errorDetails = undefined;

    const recFileDownloadState = state.recFileDownload;
    recFileDownloadState.error = false;
    recFileDownloadState.errorTitle = undefined;
    recFileDownloadState.errorDetails = undefined;

    const commentState = state.comment;
    commentState.error = false;
    commentState.errorTitle = undefined;
    commentState.errorDetails = undefined;

    const displayableState = state.displayable;
    displayableState.error = false;
    displayableState.errorTitle = undefined;
    displayableState.errorDetails = undefined;

    const registerLoginState = state.registerLogin;
    registerLoginState.error = false;
    registerLoginState.errorTitle = undefined;
    registerLoginState.errorDetails = undefined;
};

export const mutations: MutationTree<PrivilegeIdLendingRecordState> = {
    // エラークリア
    [types.CLEAR_LENDING_RECORDS_ERROR](state) {
        const privilegeIdLendingRecordsState = state.privilegeIdLendingRecords;
        privilegeIdLendingRecordsState.error = false;
        privilegeIdLendingRecordsState.errorTitle = undefined;
        privilegeIdLendingRecordsState.errorDetails = undefined;
    },

    // 特権ID貸出履歴検索系
    [types.SEARCH_START](state) {
        const searchState = state.search;
        searchState.error = false;
        searchState.errorCode = undefined;
        searchState.errorMsg = undefined;
        searchState.isProcessing = true;
        searchState.isFetchSuccess = false;
    },

    [types.SEARCH_SUCCESS](state, payload: { count: number; records: Array<PrivilegeIdLendingRecord> }) {
        state.LendingRecordsCount = payload.count;
        state.LendingRecords = payload.records;

        const searchState = state.search;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = true;
    },

    [types.SEARCH_FAILURE](state, payload: { code: string; message: string }) {
        const searchState = state.search;
        searchState.errorCode = payload.code;
        searchState.errorMsg = payload.message;
        searchState.error = true;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = false;
    },

    // 特権ID貸出履歴の詳細取得系
    [types.SHOW_START](state) {
        resetErrors(state);
        const showState = state.show;
        showState.isProcessing = true;
        showState.isFetchSuccess = false;
    },

    [types.SHOW_SUCCESS](state, payload: PrivilegeIdLendingRecordEdit) {
        state.LendingRecordsDetails = payload;

        const showState = state.show;
        showState.isProcessing = false;
        showState.isFetchSuccess = true;
    },

    [types.SHOW_FAILURE](state, payload: { code: string; message: string }) {
        const showState = state.show;
        showState.errorCode = payload.code;
        showState.errorMsg = payload.message;
        showState.error = true;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;
    },

    // CSV出力系
    [types.CSV_OUTPUT_START](state) {
        resetErrors(state);
        const csvState = state.csv;
        csvState.isProcessing = true;
    },

    [types.CSV_OUTPUT_SUCCESS](state) {
        const csvState = state.csv;
        csvState.isProcessing = false;
    },

    [types.CSV_OUTPUT_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const csvState = state.csv;
        csvState.errorTitle = payload.errorTitle;
        csvState.errorDetails = payload.errorDetails;
        csvState.error = true;
        csvState.isProcessing = false;
    },

    // 作業記録データ動画ファイルの取得処理系
    [types.MOVIE_FILE_START](state) {
        resetErrors(state);
        const videoFileState = state.videoFile;
        videoFileState.error = false;
        videoFileState.errorTitle = undefined;
        videoFileState.errorDetails = undefined;
        videoFileState.isProcessing = true;
        videoFileState.isFetchSuccess = false;
    },

    [types.MOVIE_FILE_SUCCESS](state, payload: { file_path: string }) {
        state.movieFilePath = payload.file_path;

        const videoFileState = state.videoFile;
        videoFileState.isProcessing = false;
        videoFileState.isFetchSuccess = true;
    },

    [types.MOVIE_FILE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const videoFileState = state.videoFile;
        videoFileState.errorDetails = payload.errorDetails;
        videoFileState.errorTitle = payload.errorTitle;
        videoFileState.error = true;
        videoFileState.isProcessing = false;
        videoFileState.isFetchSuccess = false;
    },

    // 作業記録ログファイルと検知情報の取得系
    [types.TEXT_FILE_START](state) {
        const textFileState = state.textFile;
        textFileState.error = false;
        textFileState.errorTitle = undefined;
        textFileState.errorDetails = undefined;
        textFileState.isProcessing = true;
        textFileState.isFetchSuccess = false;
    },

    [types.TEXT_FILE_SUCCESS](state, payload: { text_file_details: TextFile }) {
        state.textFileDetails = payload.text_file_details;

        const textFileState = state.textFile;
        textFileState.isProcessing = false;
        textFileState.isFetchSuccess = true;
    },

    [types.TEXT_FILE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const textFileState = state.textFile;
        textFileState.errorDetails = payload.errorDetails;
        textFileState.errorTitle = payload.errorTitle;
        textFileState.error = true;
        textFileState.isProcessing = false;
        textFileState.isFetchSuccess = false;
    },

    // 作業記録RECファイルの取得系
    [types.REC_FILE_START](state) {
        resetErrors(state);
        const recFileState = state.recFile;
        recFileState.error = false;
        recFileState.errorTitle = undefined;
        recFileState.errorDetails = undefined;
        recFileState.isProcessing = true;
        recFileState.isFetchSuccess = false;
    },

    [types.REC_FILE_SUCCESS](state, payload) {
        state.recFilePath = payload;

        const recFileState = state.recFile;
        recFileState.isProcessing = false;
        recFileState.isFetchSuccess = true;
    },

    [types.REC_FILE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const recFileState = state.recFile;
        recFileState.errorDetails = payload.errorDetails;
        recFileState.errorTitle = payload.errorTitle;
        recFileState.error = true;
        recFileState.isProcessing = false;
        recFileState.isFetchSuccess = false;
    },

    // 特権ID貸出記録登録(PW・鍵取得)系
    [types.PRIVILEGE_ID_LENDING_RECORDS_START](state) {
        const privilegeIdLendingRecordsState = state.privilegeIdLendingRecords;
        state.postPrivilegeIdLendingRecords = undefined;
        privilegeIdLendingRecordsState.error = false;
        privilegeIdLendingRecordsState.errorTitle = undefined;
        privilegeIdLendingRecordsState.errorDetails = undefined;
        privilegeIdLendingRecordsState.isProcessing = true;
        privilegeIdLendingRecordsState.isFetchSuccess = false;
    },

    [types.PRIVILEGE_ID_LENDING_RECORDS_SUCCESS](state, payload: PostPrivilegeIdLendingRecords) {
        const privilegeIdLendingRecordsState = state.privilegeIdLendingRecords;
        state.postPrivilegeIdLendingRecords = payload;
        privilegeIdLendingRecordsState.isProcessing = false;
        privilegeIdLendingRecordsState.isFetchSuccess = true;
    },

    [types.PRIVILEGE_ID_LENDING_RECORDS_FAILURE](
        state,
        payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }
    ) {
        const privilegeIdLendingRecordsState = state.privilegeIdLendingRecords;
        privilegeIdLendingRecordsState.errorTitle = payload.errorTitle;
        privilegeIdLendingRecordsState.errorDetails = payload.errorDetails;
        privilegeIdLendingRecordsState.error = true;
        privilegeIdLendingRecordsState.isProcessing = false;
        privilegeIdLendingRecordsState.isFetchSuccess = false;
    },

    // 定常作業の特権ID貸出記録登録(PW・鍵取得)系
    [types.ROUTINE_WORK_START](state) {
        const routineWorkState = state.routineWork;
        state.postPrivilegeIdLendingRecords = undefined;
        routineWorkState.error = false;
        routineWorkState.errorTitle = undefined;
        routineWorkState.errorDetails = undefined;
        routineWorkState.isProcessing = true;
        routineWorkState.isFetchSuccess = false;
    },

    [types.ROUTINE_WORK_SUCCESS](state, payload: PostRoutineWork) {
        const routineWorkState = state.routineWork;
        state.postRoutineWork = payload;
        routineWorkState.isProcessing = false;
        routineWorkState.isFetchSuccess = true;
    },

    [types.ROUTINE_WORK_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const routineWorkState = state.routineWork;
        routineWorkState.errorTitle = payload.errorTitle;
        routineWorkState.errorDetails = payload.errorDetails;
        routineWorkState.error = true;
        routineWorkState.isProcessing = false;
        routineWorkState.isFetchSuccess = false;
    },

    // RECファイルダウンロード系
    [types.REC_FILE_DOWNLOAD_START](state) {
        resetErrors(state);
        const recFileDownloadState = state.recFileDownload;
        recFileDownloadState.isProcessing = true;
    },

    [types.REC_FILE_DOWNLOAD_SUCCESS](state) {
        const recFileDownloadState = state.recFileDownload;
        recFileDownloadState.isProcessing = false;
    },

    [types.REC_FILE_DOWNLOAD_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const recFileDownloadState = state.recFileDownload;
        recFileDownloadState.errorTitle = payload.errorTitle;
        recFileDownloadState.errorDetails = payload.errorDetails;
        recFileDownloadState.error = true;
        recFileDownloadState.isProcessing = false;
    },

    // FATALエラー系
    [types.FATAL_FAILURE](state) {
        const searchState = state.search;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = false;

        const showState = state.show;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;

        const csvState = state.csv;
        csvState.isProcessing = false;

        const videoFileState = state.videoFile;
        videoFileState.isProcessing = false;
        videoFileState.isFetchSuccess = false;

        const textFileState = state.textFile;
        textFileState.isProcessing = false;
        textFileState.isFetchSuccess = false;

        const recFileState = state.recFile;
        recFileState.isProcessing = false;
        recFileState.isFetchSuccess = false;

        const privilegeIdLendingRecordsState = state.privilegeIdLendingRecords;
        privilegeIdLendingRecordsState.isProcessing = false;
        privilegeIdLendingRecordsState.isFetchSuccess = false;

        const routineWorkState = state.routineWork;
        routineWorkState.isProcessing = false;
        routineWorkState.isFetchSuccess = false;

        const recFileDownloadState = state.recFileDownload;
        recFileDownloadState.isProcessing = false;

        const registerLoginState = state.registerLogin;
        registerLoginState.isProcessing = false;
        registerLoginState.isFetchSuccess = false;
    },

    [types.CLEAR_ALL](state) {
        resetErrors(state);

        state.movieFilePath = undefined;
        state.textFileDetails = undefined;
        state.recFilePath = undefined;
        state.LendingRecords = undefined;
        state.LendingRecordsCount = 0;
        state.LendingRecordsDetails = undefined;
        state.postPrivilegeIdLendingRecords = undefined;
        state.postRoutineWork = undefined;
    },

    [types.COMMENT_START](state) {
        const commentState = state.comment;
        commentState.isProcessing = true;
    },

    [types.COMMENT_SUCCESS](state) {
        const commentState = state.comment;
        commentState.isProcessing = false;
    },

    [types.COMMENT_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const commentState = state.comment;
        commentState.errorTitle = payload.errorTitle;
        commentState.errorDetails = payload.errorDetails;
        commentState.error = true;
        commentState.isProcessing = false;
    },

    // 特権ID貸出履歴の参照権限取得系
    [types.DISPLAYABLE_START](state) {
        resetErrors(state);
        const showState = state.displayable;
        showState.isProcessing = true;
        showState.isFetchSuccess = false;
    },

    [types.DISPLAYABLE_SUCCESS](state, payload: { displayable: boolean }) {
        state.displayable.isDisplayable = payload.displayable;

        const displayableState = state.displayable;
        displayableState.isProcessing = false;
        displayableState.isFetchSuccess = true;
    },

    [types.DISPLAYABLE_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const displayableState = state.displayable;
        displayableState.errorTitle = payload.errorTitle;
        displayableState.errorDetails = payload.errorDetails;
        displayableState.error = true;
        displayableState.isProcessing = false;
        displayableState.isFetchSuccess = false;
    },

    // ノードログイン記録系
    [types.REGISTER_LOGIN_START](state) {
        resetErrors(state);
        const registerLoginState = state.registerLogin;
        registerLoginState.isProcessing = true;
        registerLoginState.isFetchSuccess = false;
    },

    [types.REGISTER_LOGIN_SUCCESS](state) {
        const registerLoginState = state.registerLogin;
        registerLoginState.isProcessing = false;
        registerLoginState.isFetchSuccess = true;
    },

    [types.REGISTER_LOGIN_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const registerLoginState = state.registerLogin;
        registerLoginState.errorTitle = payload.errorTitle;
        registerLoginState.errorDetails = payload.errorDetails;
        registerLoginState.error = true;
        registerLoginState.isProcessing = false;
        registerLoginState.isFetchSuccess = false;
    },
};
